<template>
  <main class="comp-def-page">
    <CompanyBreadcrumb :aria-label="$t('general.competency_and_user_def')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <div class="section-header mb-lg-2 border-0 uk-flex-middle comp-def-header">
      <div class="section-header-left" />
      <div class="section-header-right comp-def-header-actions">
        <button aria-expanded="false" class="btn btn-outline-primary comp-def-action-btn" type="button" @click="openUserAssignmentModal">
          <i class="uil-user-check"></i> {{ $t('general.define_to_users') }}
        </button>

        <button v-if="$hasPermission('create_competency')" aria-expanded="false" class="btn btn-default comp-def-action-btn" type="button"
          @click="openAddModal">
          <i class="uil-plus"></i> {{ $t('general.add_competency') }}
        </button>

        <button v-else aria-expanded="false" class="btn btn-default comp-def-action-btn" type="button" disabled
          :style="{ cursor: 'not-allowed' }" :uk-tooltip="$t('general.no_permission')">
          <i class="uil-plus"></i> {{ $t('general.add_competency') }}
        </button>
      </div>
    </div>

    <template v-if="errorMessages">
      <div v-for="(error, index) in errorMessages" :key="index" class="card comp-def-alert-card">
        <div class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p style="color: red">
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </div>
    </template>
    <div v-if="successMessage" class="card comp-def-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: green">
          <i class="uil-check"></i> {{ successMessage }}
        </p>
      </div>
    </div>

    <div class="card comp-def-card">
      <!-- Card header -->
      <div class="card-header actions-toolbar border-0 comp-def-toolbar">
        <div class="d-flex justify-content-between comp-def-toolbar-inner">
          <div class="comp-def-search">
            <div class="uk-search uk-search-navbar comp-def-search-field">
              <span class="uk-search-icon comp-def-search-icon" aria-hidden="true">
                <i class="uil-search"></i>
              </span>
              <input v-model="filter.search" :placeholder="$t('general.search')"
                :aria-label="$t('general.search')"
                class="uk-search-input shadow-0 uk-form-small" type="search" />
            </div>
          </div>
          <div class="d-flex comp-def-toolbar-actions">
            <sort-dropdown :sort-options="sortOption" :title="sortTitle" @sort-selected="sortOrder" />
          </div>
        </div>
      </div>
      <!-- Table -->
      <div class="sf-table comp-def-table-wrap">
        <div v-if="isLoading" class="text-center p-4">
          <default-loading />
        </div>
        <table v-else>
          <thead>
            <tr>
              <th scope="col"> {{ $t('general.competency_name') }}</th>
              <th scope="col"> {{ $t('general.description') }}</th>
              <th scope="col"> {{ $t('general.created_by') }}</th>
              <th scope="col"> {{ $t('excel.heading.content_count') }}</th>
              <th scope="col"> {{ $t('general.assignment_count') }}</th>
              <th scope="col">{{ $t("general.added_at") }}</th>
              <th class="sf-table__col-actions" scope="col"></th>
            </tr>
          </thead>
          <tbody class="list">
            <!--competency item-->
            <tr v-if="formattedItems.length === 0" class="text-center">
              <td class="sf-table__empty" colspan="7">
                <div class="text-muted">
                  <i class="uil-inbox" style="font-size: 2rem;"></i>
                  <p class="mt-2"> {{ $t('general.no_have_competency') }} </p>
                </div>
              </td>
            </tr>
            <tr v-for="competency in formattedItems" :key="competency.id">
              <td>{{ competency.name }}</td>
              <td>{{ competency.description }}</td>
              <td>{{ competency.created_by_name }}</td>
              <td>{{ competency.contents_count }}</td>
              <td>{{ competency.assignments_count }}</td>
              <td>{{ competency.created_at }}</td>
              <td class="sf-table__col-actions">
                <div class="sf-table__actions comp-def-row-actions">
                  <button :uk-tooltip="$t('general.edit')" class="btn btn-icon btn-hover btn-sm btn-circle"
                    @click="openEditModal(competency)">
                    <i class="uil-edit "></i>
                  </button>
                  <button :uk-tooltip="$t('general.delete')" class="btn btn-icon btn-hover btn-sm btn-circle"
                    @click="deleteCompetency(competency.id)">
                    <i class="uil-minus-circle text-danger"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <pagination v-if="pagination && pagination.links && pagination.links.length > 0" :pagination="pagination"
      @changePage="setPage($event)"></pagination>

    <!-- Competency Modal -->
    <CompetencyAddUpdateModal ref="competencyModal" @success="onModalSuccess" />

    <!-- User Competency Assignment Modal - Lazy Loaded -->
    <UserCompetencyAssignmentModal v-if="showUserAssignmentModal" ref="userAssignmentModal"
      @success="onUserAssignmentSuccess" />
  </main>
</template>

<script>
import store from "@/core/services";

import Pagination from "@/assets/components/Pagination.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";

import moment from "moment";
import competencies_module, {
  MODULE_NAME as COMPETENCIES_MODULE_NAME,
  BASE_URL,
  LOADING,
  ERROR,
  SUCCESS,
  ITEMS,
  GET_ITEMS,
  DELETE_ITEM,
  RESET_VALUES,
} from "@/core/services/store/competencies/competencies.module";

import _ from 'lodash';

import { confirmDialogDelete } from "@/utils/confirmDialog";
import { showSuccess, showError } from "@/utils/alertUtils";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import CompetencyAddUpdateModal from "../components/CompetencyAddUpdateModal.vue";
import UserCompetencyAssignmentModal from "../components/UserCompetencyAssignmentModal.vue";

const COMPETENCIES_MODULE = COMPETENCIES_MODULE_NAME;

export default {
  name: "CompetenciesAndUserDefinitions",
  components: { SortDropdown, DefaultLoading, Pagination, CompetencyAddUpdateModal, UserCompetencyAssignmentModal, CompanyBreadcrumb },
  data() {
    return {
      page: 1,
      per_page: 15,
      sort_by: "id",
      sort_desc: "desc",
      showUserAssignmentModal: false,
      filter: {
        search: null,
        order_by: "id",
        sort: "desc",
      },
      sortTitle: this.$t("filters.order.sort"),
      sortOption: [
        { key: 'text-asc', sort: 'asc', order: 'id', label: this.$t("sorting.a_z") },
        { key: 'text-desc', sort: 'desc', order: 'id', label: this.$t("sorting.z_a") },
        { key: 'created_at-asc', sort: 'asc', order: 'created_at', label: this.$t("sorting.created_at_asc") },
        { key: 'created_at-desc', sort: 'desc', order: 'created_at', label: this.$t("sorting.created_at_desc") },
      ],
    };
  },

  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(COMPETENCIES_MODULE, competencies_module);
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("general.competency_and_user_def") },
      ];
    },
    isLoading() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + LOADING];
    },
    hasError() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + ERROR];
    },
    errorMessage() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + ERROR];
    },
    successMessage() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + SUCCESS];
    },
    allCompetencies() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + ITEMS];
    },
    pagination() {
      const items = this.$store.getters[COMPETENCIES_MODULE + '/' + ITEMS];
      return items?.meta || null;
    },
    formattedItems() {
      let results = [];
      let competencies = this.allCompetencies;

      // Handle different response structures
      if (competencies && competencies.data) {
        competencies = competencies.data;
      }

      if (!competencies || !Array.isArray(competencies)) return [];

      competencies.forEach((competency) => {
        results.push({
          ...competency,
          description: competency.description || '-',
          created_by_name: competency.created_by ?
            `${competency.created_by.name} ${competency.created_by.surname}` : '-',
          contents_count: competency.contents_count || 0,
          assignments_count: competency.assignments_count || 0,
          created_at: competency.created_at
            ? moment(competency.created_at).format("DD/MM/YYYY HH:mm:ss")
            : "-",
        });
      });

      return results;
    },
    errorMessages() {
      return this.hasError ? [this.errorMessage] : [];
    }
  },
  methods: {
    async fetchCompetencies(filters) {
      try {
        await this.$store.dispatch(COMPETENCIES_MODULE + '/' + GET_ITEMS, {
          filters: filters
        });
      } catch (error) {
        console.error('Fetch competencies error:', error);
        throw error;
      }
    },
    async deleteCompetency(id) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (!isConfirmed) return;

      try {
        await this.$store.dispatch(COMPETENCIES_MODULE + '/' + DELETE_ITEM,
          { url: `${BASE_URL}/${id}` }
        );

        // Error'u temizle
        this.clearMessages();

        showSuccess(this.$t('general.successfully_deleted'), 3000);

        await this.getCompetenciesResult();
      } catch (error) {
        this.clearMessages();
        console.error("Delete error:", error);
        showError(this.$t(error), 8000);
      }
    },

    clearMessages() {
      this.$store.commit(COMPETENCIES_MODULE + '/' + RESET_VALUES);
    },
    async getCompetenciesResult() {
      try {
        const filters = {
          page: this.page,
        };

        if (this.filter.search) {
          filters.search = this.filter.search;
        }

        if (this.filter.order_by && this.filter.sort) {
          filters.order_by = this.filter.order_by;
          filters.sort = this.filter.sort;
        }

        await this.fetchCompetencies(filters);

        // Check if fetch was successful
        if (this.hasError) {
          showError(this.errorMessage || this.$t('general.error_occurred_while_loading_competencies'), 5000);
        }
      } catch (error) {
        console.error('Competencies fetch error:', error);
        showError(this.$t('general.error_occurred_while_loading_competencies'), 5000);
      }
    },

    sortOrder({ order, sort }) {
      this.page = 1;
      this.filter.order_by = order;
      this.filter.sort = sort;
      this.getCompetenciesResult();
    },
    setPage(page) {
      this.page = page;
      this.getCompetenciesResult();
    },
    resetMessages() {
      this.clearMessages();
    },

    openAddModal() {
      this.$nextTick(() => {
        this.$refs.competencyModal.openModal(null);
      });
    },

    openEditModal(competency) {
      this.$nextTick(() => {
        this.$refs.competencyModal.openModal(competency);
      });
    },

    onModalSuccess() {
      this.getCompetenciesResult();
    },

    async openUserAssignmentModal() {
      this.showUserAssignmentModal = true;
      await this.$nextTick();
      await this.$refs.userAssignmentModal.openModal();
    },

    onUserAssignmentSuccess() {
      // Kullanıcı atama başarılı olduğunda yapılacak işlemler
    },
  },
  mounted() {
    this.getCompetenciesResult();
  },
  beforeDestroy() {
    this.clearMessages();
  },
  watch: {
    "filter.search": function (val) {
      this.debouncedFilterSearch();
    },
    successMessage: function (val) {
      if (val) {
        this.debouncedResetValues();
      }
    },
    errorMessage: function (val) {
      if (val) {
        this.debouncedResetValues();
      }
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getCompetenciesResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>
<style scoped>
.comp-def-page {
  --comp-def-primary: var(--primaryColor, #3e416d);
  --comp-def-text: #1e293b;
  --comp-def-muted: #64748b;
  --comp-def-border: #e2e8f0;
  min-width: 0;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

.comp-def-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.comp-def-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comp-def-action-btn {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.comp-def-alert-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--comp-def-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.comp-def-page .uk-alert {
  margin: 0;
}

.comp-def-card {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  margin-top: 0;
  background: #fff;
  border: 1px solid var(--comp-def-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.comp-def-toolbar {
  padding: 16px;
}

.comp-def-toolbar-inner {
  align-items: center;
  gap: 16px;
}

.comp-def-search {
  flex: 1 1 auto;
  min-width: 0;
}

.comp-def-search-field,
.comp-def-search-field.uk-search-navbar {
  display: block;
  position: relative;
  width: 100%;
  max-width: 100%;
}

.comp-def-search-icon,
.comp-def-page ::v-deep .comp-def-search-icon.uk-search-icon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  margin: 0;
  pointer-events: none;
  color: #3f87f5;
  font-size: 20px;
  line-height: 1;
}

.comp-def-search-icon i.uil-search {
  position: static;
  top: auto;
  left: auto;
  font-size: 20px;
  line-height: 1;
}

.comp-def-search .uk-search-input {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 16px 10px 40px !important;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  background: #fff;
  color: var(--comp-def-text);
  font-size: 14px !important;
  line-height: 1.4;
}

.comp-def-search .uk-search-input:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.25);
  outline-offset: 2px;
}

.comp-def-toolbar-actions {
  align-items: center;
  gap: 8px;
}

/* Tablo tasarimi .sf-table'dan gelir (assets/scss/_data-table.scss).
   Bu sayfada zebra YOK, satir alt cizgisi kullaniliyor — sistemin
   varsayilani da bu, bu yuzden --zebra modifier'i eklenmedi. */

.comp-def-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.comp-def-row-actions .btn-circle {
  width: 30px;
  height: 30px;
}

.comp-def-action-btn:active,
.comp-def-row-actions .btn-circle:active {
  transform: scale(0.98);
  opacity: 0.85;
}

@media (max-width: 959px) {
  .comp-def-page {
    padding: 0 0 24px;
  }

  .comp-def-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .comp-def-header-actions {
    flex-direction: column;
    gap: 8px;
  }

  .comp-def-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .comp-def-toolbar-inner {
    flex-direction: column;
    align-items: stretch !important;
  }

  .comp-def-search {
    width: 100%;
  }

  .comp-def-toolbar-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .comp-def-search-icon,
  .comp-def-page ::v-deep .comp-def-search-icon.uk-search-icon {
    width: 48px;
  }

  .comp-def-search .uk-search-input {
    width: 100%;
    height: 48px;
    min-height: 48px;
    padding: 12px 16px 12px 48px !important;
    font-size: 16px !important;
    line-height: 1.4;
  }

  /* Tablo mobilde sikistirilmaz, yatay kaydirilir */
  .comp-def-table-wrap {
    --sf-table-min-width: 900px;
  }

  .comp-def-row-actions .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }
}

@media (max-width: 767px) {
  .comp-def-page {
    padding: 0 0 24px;
  }

  .comp-def-card {
    border-radius: 14px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .comp-def-action-btn:hover,
  .comp-def-row-actions .btn-circle:hover {
    cursor: pointer;
  }
}

@media (hover: none) {
  .comp-def-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .comp-def-action-btn:active,
  .comp-def-row-actions .btn-circle:active {
    transform: none;
  }
}
</style>
